import { useEffect, useMemo, useState } from 'react';
import type { RoleComputePresetDTO } from '../../../types/auth';
import type {
  CheckoutCardFields,
  CheckoutProceedPayload,
  CheckoutSavedPaymentPayload,
} from '../../../types/billing';
import { PlanMetricRow } from '../../../components/PlanCardShell';
import {
  displayMonthlyUsdForCadence,
  formatCount,
  type BillingCadenceDisplay,
  type ProfileCurrency,
} from '../../../config/billingProfile';

export type CheckoutLimitMode = 'daily' | 'monthly' | 'unlimited';

type ConfirmPlanCheckoutProps = {
  limitMode: CheckoutLimitMode;
  pr: RoleComputePresetDTO;
  currency: ProfileCurrency;
  billingCadence: BillingCadenceDisplay;
  monthlyExtraHours: number;
  onMonthlyExtraHoursChange: (hours: number) => void;
  totalMonthlyHours: number;
  extraHoursCostUsd: number;
  formatMoney: (amountUsd: number, c: ProfileCurrency) => string;
  onChangePlan: () => void;
  onBack: () => void;
  /** Card checkout — includes auto-renew preference for confirm API. */
  onProceed: (payload: CheckoutProceedPayload) => void | Promise<void>;
  proceedLoading?: boolean;
  defaultBillingEmail?: string | null;
  /** When set with `onProceedWithSavedPayment`, checkout offers one-tap vault attach (no PAN in browser). */
  savedPaymentMethodEligible?: boolean;
  savedPaymentMethodLabel?: string;
  onProceedWithSavedPayment?: (payload: CheckoutSavedPaymentPayload) => void | Promise<void>;
  /** Increment to force "Use a different card" (e.g. PayMongo 409 on saved attach). */
  vaultAttachConflictSignal?: number;
};

export default function ConfirmPlanCheckout({
  limitMode,
  pr,
  currency,
  billingCadence,
  monthlyExtraHours,
  onMonthlyExtraHoursChange,
  totalMonthlyHours,
  extraHoursCostUsd,
  formatMoney,
  onChangePlan,
  onBack,
  onProceed,
  proceedLoading = false,
  defaultBillingEmail = '',
  savedPaymentMethodEligible = false,
  savedPaymentMethodLabel = '',
  onProceedWithSavedPayment,
  vaultAttachConflictSignal = 0,
}: ConfirmPlanCheckoutProps) {
  const savedChoiceAvailable = useMemo(
    () => !!(savedPaymentMethodEligible && onProceedWithSavedPayment),
    [savedPaymentMethodEligible, onProceedWithSavedPayment],
  );
  const [checkoutPayMode, setCheckoutPayMode] = useState<'saved' | 'new'>(() =>
    savedChoiceAvailable ? 'saved' : 'new',
  );
  const [cardNumberVis, setCardNumberVis] = useState('');
  const [cardExpiry, setCardExpiry] = useState('');
  const [cardCvc, setCardCvc] = useState('');
  const [cardName, setCardName] = useState('');
  const [cardEmail, setCardEmail] = useState(() => (defaultBillingEmail || '').trim());
  const [billingLine1, setBillingLine1] = useState('');
  const [billingLine2, setBillingLine2] = useState('');
  const [billingCity, setBillingCity] = useState('');
  const [billingState, setBillingState] = useState('');
  const [billingPostal, setBillingPostal] = useState('');
  const [billingCountry, setBillingCountry] = useState('PH');
  const [billingPhone, setBillingPhone] = useState('');
  const [billingAutoRenew, setBillingAutoRenew] = useState(true);
  const [checkoutFieldError, setCheckoutFieldError] = useState<string | null>(null);

  useEffect(() => {
    const e = (defaultBillingEmail || '').trim();
    if (e) setCardEmail((prev) => (prev.trim() ? prev : e));
  }, [defaultBillingEmail]);

  useEffect(() => {
    setCheckoutPayMode(savedChoiceAvailable ? 'saved' : 'new');
  }, [savedChoiceAvailable]);

  useEffect(() => {
    if (vaultAttachConflictSignal > 0) {
      setCheckoutPayMode('new');
    }
  }, [vaultAttachConflictSignal]);

  const paymentInputClass =
    'w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-emerald-500/40 focus:outline-none focus:ring-2 focus:ring-emerald-500/15 disabled:cursor-not-allowed disabled:bg-gray-50';

  const proceedWithSavedVaultedCard = () => {
    setCheckoutFieldError(null);
    if (!onProceedWithSavedPayment) return;
    void onProceedWithSavedPayment({ billing_auto_renew: billingAutoRenew });
  };

  const handleProceed = () => {
    setCheckoutFieldError(null);
    let digits: string;
    try {
      digits = cardNumberVis.normalize('NFKC').replace(/\D/g, '');
    } catch {
      digits = cardNumberVis.replace(/\D/g, '');
    }
    if (digits.length < 12 || digits.length > 19) {
      setCheckoutFieldError('Enter a valid card number (12–19 digits; test mode — use PayMongo’s published test PANs).');
      return;
    }
    if (!/^\d{1,2}\s*\/\s*(\d{2}|\d{4})$/.test(cardExpiry.trim())) {
      setCheckoutFieldError('Use expiry as mm/yyyy (e.g. 12/2030).');
      return;
    }
    if (!cardCvc.trim() || cardCvc.trim().length < 3) {
      setCheckoutFieldError('Enter the security code (CVC/CVV).');
      return;
    }
    if (!cardName.trim()) {
      setCheckoutFieldError('Enter the name on the card.');
      return;
    }
    const line1 = billingLine1.trim();
    const city = billingCity.trim();
    const postal = billingPostal.trim();
    const cc = billingCountry.trim();
    if (!line1) {
      setCheckoutFieldError('Enter street address line 1 (billing address).');
      return;
    }
    if (!city) {
      setCheckoutFieldError('Enter city for your billing address.');
      return;
    }
    if (!postal) {
      setCheckoutFieldError('Enter postal / ZIP code for your billing address.');
      return;
    }
    const countryNorm = cc.toUpperCase();
    if (!/^[A-Z]{2}$/.test(countryNorm)) {
      setCheckoutFieldError('Country must be a 2-letter ISO code (e.g. PH, US).');
      return;
    }
    void onProceed({
      card: {
        cardNumberVis,
        expiry: cardExpiry,
        cvc: cardCvc,
        nameOnCard: cardName,
        billingEmail: cardEmail.trim() || undefined,
        billingAddress: {
          line1,
          ...(billingLine2.trim() ? { line2: billingLine2.trim() } : {}),
          city,
          ...(billingState.trim() ? { state: billingState.trim() } : {}),
          postal_code: postal,
          country: countryNorm,
        },
        ...(billingPhone.trim() ? { billingPhone: billingPhone.trim() } : {}),
      },
      billing_auto_renew: billingAutoRenew,
    });
  };

  return (
    <div className="mx-auto max-w-5xl">
      <header className="mb-6 text-center sm:mb-8">
        <h1 id="confirm-plan-title" className="text-2xl font-bold tracking-tight text-gray-900 sm:text-[1.65rem]">Confirm Plan</h1>
        <p className="mt-2 text-sm italic text-gray-600">Review and complete your subscription</p>
      </header>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-12 lg:gap-6">
        {/* Chosen plan */}
        <div className="rounded-2xl border border-gray-200/90 bg-white p-5 shadow-sm lg:col-span-5 lg:p-6">
          <h2 className="text-base font-bold text-gray-900">Chosen Plan</h2>
          <div className="mt-4 rounded-xl bg-gray-100 px-4 py-3 text-center text-xs text-gray-600">
            {billingCadence === 'monthly'
              ? 'Billed monthly. Cancel anytime.'
              : 'Billed annually with 20% savings vs the monthly reference rate. Cancel anytime.'}
          </div>

          <div className="mt-5 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm ring-1 ring-black/[0.03]">
            {limitMode === 'daily' ? (
              <ChosenDaily
                pr={pr}
                currency={currency}
                formatMoney={formatMoney}
                billingCadence={billingCadence}
              />
            ) : limitMode === 'monthly' ? (
              <ChosenMonthly
                pr={pr}
                currency={currency}
                formatMoney={formatMoney}
                billingCadence={billingCadence}
                monthlyExtraHours={monthlyExtraHours}
                onMonthlyExtraHoursChange={onMonthlyExtraHoursChange}
                totalMonthlyHours={totalMonthlyHours}
                extraHoursCostUsd={extraHoursCostUsd}
              />
            ) : (
              <ChosenUnlimited
                pr={pr}
                currency={currency}
                formatMoney={formatMoney}
                billingCadence={billingCadence}
              />
            )}
          </div>

          <button
            type="button"
            onClick={onChangePlan}
            className="mt-5 w-full text-center text-sm font-semibold text-blue-700 underline underline-offset-2 hover:text-blue-900"
          >
            Change Plan
          </button>
        </div>

        {/* Payment — card */}
        <div className="rounded-2xl border border-gray-200/90 bg-white p-5 shadow-sm lg:col-span-7 lg:p-6">
          <h2 className="text-base font-bold text-gray-900">Payment</h2>
          <p className="mt-1 text-sm text-gray-600">
            {checkoutPayMode === 'saved' && savedChoiceAvailable
              ? 'We charge the card Digisaka already vaulted with PayMongo — no typing your card again here.'
              : 'Pay by credit or debit card. With a new card, details go from your browser to PayMongo only, not through Digisaka.'}
          </p>

          {savedChoiceAvailable ? (
            <div
              role="tablist"
              aria-label="Payment method"
              className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-2"
            >
              <button
                type="button"
                role="tab"
                aria-selected={checkoutPayMode === 'saved'}
                onClick={() => setCheckoutPayMode('saved')}
                disabled={proceedLoading}
                className={`rounded-xl border px-4 py-3 text-left text-sm font-semibold transition ${
                  checkoutPayMode === 'saved'
                    ? 'border-emerald-600 bg-emerald-50 text-emerald-950 ring-2 ring-emerald-500/35'
                    : 'border-gray-200 bg-gray-50/80 text-gray-800 hover:border-gray-300'
                }`}
              >
                Use existing card
                {savedPaymentMethodLabel.trim() ? (
                  <span className="mt-1 block text-xs font-semibold uppercase tracking-wide text-emerald-800/95">
                    {savedPaymentMethodLabel.trim()}
                  </span>
                ) : (
                  <span className="mt-1 block text-[11px] font-normal capitalize text-emerald-800/85">
                    On file · PayMongo
                  </span>
                )}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={checkoutPayMode === 'new'}
                onClick={() => setCheckoutPayMode('new')}
                disabled={proceedLoading}
                className={`rounded-xl border px-4 py-3 text-left text-sm font-semibold transition ${
                  checkoutPayMode === 'new'
                    ? 'border-slate-800 bg-slate-900 text-white ring-2 ring-slate-500/35'
                    : 'border-gray-200 bg-gray-50/80 text-gray-800 hover:border-gray-300'
                }`}
              >
                Use a different card
                <span className="mt-1 block text-[11px] font-normal text-gray-500">
                  Visa · Mastercard (browser → PayMongo)
                </span>
              </button>
            </div>
          ) : null}

          {checkoutPayMode === 'saved' && savedChoiceAvailable ? (
            <div className="mt-5 overflow-hidden rounded-xl border border-emerald-200/90 bg-emerald-50/60 shadow-sm ring-1 ring-emerald-100/80">
              <div className="border-b border-emerald-100/90 bg-emerald-900 px-4 py-3 text-white">
                <div className="flex flex-wrap items-center gap-2">
                  <i className="ri-shield-check-line text-lg opacity-95" aria-hidden />
                  <p className="text-sm font-semibold">Vaulted PayMongo payment method</p>
                </div>
              </div>
              <div className="space-y-2 p-5 text-sm leading-snug text-emerald-950">
                <p>
                  Digisaka will attach this saved instrument to today&apos;s PaymentIntent server-side — same card auto-renew
                  relies on when enabled.
                </p>
              </div>
            </div>
          ) : (
            <div className="mt-5 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
            <div className="border-b border-gray-100 bg-slate-900 px-4 py-3.5 text-white">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-3">
                  <i className="ri-bank-card-2-line text-lg opacity-95" aria-hidden />
                  <div>
                    <p className="text-sm font-semibold leading-tight">Credit or debit card</p>
                    <p className="mt-0.5 text-[11px] leading-snug text-slate-300">Visa · Mastercard · PayMongo</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wide">
                  <span className="rounded border border-white/25 bg-white/10 px-2 py-0.5 text-white">Visa</span>
                  <span className="rounded border border-white/25 bg-white/10 px-2 py-0.5 text-white">MC</span>
                </div>
              </div>
            </div>
            <div className="space-y-3 p-4 sm:p-5">
              <div className="grid gap-3 sm:grid-cols-6">
                <label className="sm:col-span-6">
                  <span className="mb-1 block text-[11px] font-medium text-gray-500">Card number</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="cc-number"
                    placeholder="0000 0000 0000 0000"
                    value={cardNumberVis}
                    onChange={(e) => setCardNumberVis(e.target.value)}
                    disabled={proceedLoading}
                    className={paymentInputClass}
                  />
                  <p className="mt-1 text-[10px] leading-snug text-gray-400">
                    Spaces OK; digits only reach PayMongo. In test mode, use{' '}
                    <a
                      href="https://developers.paymongo.com/docs/testing"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-semibold text-emerald-700 underline underline-offset-2 hover:text-emerald-900"
                    >
                      PayMongo test cards
                    </a>
                    — random PANs usually fail validation.
                  </p>
                </label>
                <label className="sm:col-span-2">
                  <span className="mb-1 block text-[11px] font-medium text-gray-500">Expiration</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="cc-exp"
                    placeholder="mm/yyyy"
                    value={cardExpiry}
                    onChange={(e) => setCardExpiry(e.target.value)}
                    disabled={proceedLoading}
                    className={paymentInputClass}
                  />
                </label>
                <label className="sm:col-span-4">
                  <span className="mb-1 block text-[11px] font-medium text-gray-500">Name on card</span>
                  <input
                    type="text"
                    autoComplete="cc-name"
                    placeholder="Full name"
                    value={cardName}
                    onChange={(e) => setCardName(e.target.value)}
                    disabled={proceedLoading}
                    className={paymentInputClass}
                  />
                </label>
                <label className="sm:col-span-2">
                  <span className="mb-1 block text-[11px] font-medium text-gray-500">Security code</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="cc-csc"
                    placeholder="CVV"
                    value={cardCvc}
                    onChange={(e) => setCardCvc(e.target.value)}
                    disabled={proceedLoading}
                    className={paymentInputClass}
                  />
                </label>
                <label className="sm:col-span-6">
                  <span className="mb-1 block text-[11px] font-medium text-gray-500">Billing email (optional)</span>
                  <input
                    type="email"
                    autoComplete="email"
                    placeholder="you@example.com"
                    value={cardEmail}
                    onChange={(e) => setCardEmail(e.target.value)}
                    disabled={proceedLoading}
                    className={paymentInputClass}
                  />
                </label>
              </div>

              <div className="border-t border-gray-100 pt-4">
                <p className="mb-2 text-[12px] font-semibold text-gray-800">Billing address</p>
                <p className="mb-3 text-[10px] leading-snug text-gray-500">
                  Should match your bank records when possible — sent to PayMongo with your payment method only.
                </p>
                <div className="grid gap-3 sm:grid-cols-6">
                  <label className="sm:col-span-6">
                    <span className="mb-1 block text-[11px] font-medium text-gray-500">
                      Street address line 1 <span className="text-emerald-600">*</span>
                    </span>
                    <input
                      type="text"
                      autoComplete="billing street-address"
                      placeholder="Street, building number"
                      value={billingLine1}
                      onChange={(e) => setBillingLine1(e.target.value)}
                      disabled={proceedLoading}
                      className={paymentInputClass}
                    />
                  </label>
                  <label className="sm:col-span-6">
                    <span className="mb-1 block text-[11px] font-medium text-gray-500">Address line 2 (optional)</span>
                    <input
                      type="text"
                      autoComplete="billing address-line2"
                      placeholder="Unit, subdivision, landmark"
                      value={billingLine2}
                      onChange={(e) => setBillingLine2(e.target.value)}
                      disabled={proceedLoading}
                      className={paymentInputClass}
                    />
                  </label>
                  <label className="sm:col-span-3">
                    <span className="mb-1 block text-[11px] font-medium text-gray-500">
                      City <span className="text-emerald-600">*</span>
                    </span>
                    <input
                      type="text"
                      autoComplete="billing address-level2"
                      placeholder="City"
                      value={billingCity}
                      onChange={(e) => setBillingCity(e.target.value)}
                      disabled={proceedLoading}
                      className={paymentInputClass}
                    />
                  </label>
                  <label className="sm:col-span-3">
                    <span className="mb-1 block text-[11px] font-medium text-gray-500">State / province (optional)</span>
                    <input
                      type="text"
                      autoComplete="billing address-level1"
                      placeholder="e.g. NCR"
                      value={billingState}
                      onChange={(e) => setBillingState(e.target.value)}
                      disabled={proceedLoading}
                      className={paymentInputClass}
                    />
                  </label>
                  <label className="sm:col-span-2">
                    <span className="mb-1 block text-[11px] font-medium text-gray-500">
                      Postal code <span className="text-emerald-600">*</span>
                    </span>
                    <input
                      type="text"
                      autoComplete="billing postal-code"
                      placeholder="ZIP / Postal"
                      value={billingPostal}
                      onChange={(e) => setBillingPostal(e.target.value)}
                      disabled={proceedLoading}
                      className={paymentInputClass}
                    />
                  </label>
                  <label className="sm:col-span-2">
                    <span className="mb-1 block text-[11px] font-medium text-gray-500">
                      Country code <span className="text-emerald-600">*</span>
                    </span>
                    <input
                      type="text"
                      autoComplete="billing country"
                      placeholder="PH"
                      maxLength={2}
                      value={billingCountry}
                      onChange={(e) =>
                        setBillingCountry(e.target.value.replace(/[^a-zA-Z]/g, '').slice(0, 2))
                      }
                      disabled={proceedLoading}
                      className={`${paymentInputClass} uppercase`}
                    />
                  </label>
                  <label className="sm:col-span-2">
                    <span className="mb-1 block text-[11px] font-medium text-gray-500">Phone (optional)</span>
                    <input
                      type="tel"
                      autoComplete="billing tel-national"
                      placeholder="+63 ..."
                      value={billingPhone}
                      onChange={(e) => setBillingPhone(e.target.value)}
                      disabled={proceedLoading}
                      className={paymentInputClass}
                    />
                  </label>
                  <p className="sm:col-span-6 text-[10px] leading-snug text-gray-400">
                    Country: ISO 3166-1 alpha-2 (two letters). Default PH — change if your card is billed outside the
                    Philippines.
                  </p>
                </div>
              </div>
              {checkoutFieldError ? (
                <p className="text-sm text-red-600" role="alert">
                  {checkoutFieldError}
                </p>
              ) : null}
            </div>
          </div>
          )}

          <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50/80 px-4 py-3">
            <label className="flex cursor-pointer items-start gap-3 text-left">
              <input
                type="checkbox"
                checked={billingAutoRenew}
                onChange={(e) => setBillingAutoRenew(e.target.checked)}
                disabled={proceedLoading}
                className="mt-0.5 accent-emerald-600"
              />
              <span className="min-w-0 text-[13px] leading-snug text-slate-800">
                <span className="font-semibold text-slate-900">Auto-renew</span> — before this plan period ends we
                automatically try to bill the same card (PayMongo). You can turn this off here or later in your profile.
              </span>
            </label>
          </div>

          <div className="mt-8 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end sm:gap-3">
            <button
              type="button"
              onClick={onBack}
              className="rounded-lg border border-gray-300 bg-white px-6 py-2.5 text-sm font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50"
            >
              Back
            </button>
            <button
              type="button"
              disabled={proceedLoading}
              aria-busy={proceedLoading}
              onClick={checkoutPayMode === 'saved' && savedChoiceAvailable ? proceedWithSavedVaultedCard : handleProceed}
              className="inline-flex min-w-[220px] items-center justify-center gap-2.5 rounded-lg bg-slate-900 px-6 py-2.5 text-sm font-semibold text-white shadow-md transition hover:bg-slate-800 disabled:cursor-wait disabled:hover:bg-slate-900"
            >
              {proceedLoading ? (
                <>
                  <span
                    className="h-[1.125rem] w-[1.125rem] shrink-0 animate-spin rounded-full border-2 border-white/35 border-t-white"
                    aria-hidden
                  />
                  <span>Processing…</span>
                </>
              ) : checkoutPayMode === 'saved' && savedChoiceAvailable ? (
                'Pay with saved card'
              ) : (
                'Proceed with payment'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function CheckoutQuotedMonthlyBlock({
  listMonthlyUsd,
  billingCadence,
  currency,
  formatMoney,
}: {
  listMonthlyUsd: number;
  billingCadence: BillingCadenceDisplay;
  currency: ProfileCurrency;
  formatMoney: (n: number, c: ProfileCurrency) => string;
}) {
  const monthlyEquiv = displayMonthlyUsdForCadence(listMonthlyUsd, billingCadence);
  const annual = billingCadence === 'annual';
  const headlineUsd = annual ? monthlyEquiv * 12 : monthlyEquiv;
  const listCompareUsd = annual ? listMonthlyUsd * 12 : listMonthlyUsd;
  return (
    <div className="space-y-1">
      {annual ? (
        <p className="text-sm tabular-nums text-gray-400 line-through">
          {formatMoney(listCompareUsd, currency)} <span className="text-xs font-normal">/yr</span>
        </p>
      ) : null}
      <p className="text-2xl font-bold text-gray-900">
        {formatMoney(headlineUsd, currency)}{' '}
        <span className="text-sm font-semibold text-gray-600">{annual ? '/yr' : '/mo'}</span>
      </p>
      {annual ? (
        <p className="text-[11px] font-semibold text-emerald-700">
          20% off vs monthly billing · ≈ {formatMoney(monthlyEquiv, currency)} /mo avg
        </p>
      ) : null}
    </div>
  );
}

function ChosenDaily({
  pr,
  currency,
  formatMoney,
  billingCadence,
}: {
  pr: RoleComputePresetDTO;
  currency: ProfileCurrency;
  formatMoney: (n: number, c: ProfileCurrency) => string;
  billingCadence: BillingCadenceDisplay;
}) {
  return (
    <>
      <div className="h-1.5 bg-gradient-to-r from-emerald-400 via-emerald-500 to-teal-500" />
      <div className="space-y-3 p-4 sm:p-5">
        <h3 className="text-[11px] font-bold uppercase tracking-wider text-teal-600">Daily Access</h3>
        <CheckoutQuotedMonthlyBlock
          listMonthlyUsd={pr.price_base_usd}
          billingCadence={billingCadence}
          currency={currency}
          formatMoney={formatMoney}
        />
        <p className="text-xs italic text-gray-500">Per UTC day allowance</p>
        <div className="rounded-xl bg-gray-50 py-4 ring-1 ring-inset ring-gray-100">
          <p className="text-center text-[10px] font-bold uppercase tracking-wider text-gray-400">Hours per UTC day</p>
          <p className="mt-2 text-center text-3xl font-bold text-teal-600">{formatCount(pr.daily_hours)}</p>
          <p className="text-center text-xs text-gray-500">hr/day</p>
        </div>
      </div>
    </>
  );
}

function ChosenMonthly({
  pr,
  currency,
  formatMoney,
  billingCadence,
  monthlyExtraHours,
  onMonthlyExtraHoursChange,
  totalMonthlyHours,
  extraHoursCostUsd,
}: {
  pr: RoleComputePresetDTO;
  currency: ProfileCurrency;
  formatMoney: (n: number, c: ProfileCurrency) => string;
  billingCadence: BillingCadenceDisplay;
  monthlyExtraHours: number;
  onMonthlyExtraHoursChange: (hours: number) => void;
  totalMonthlyHours: number;
  extraHoursCostUsd: number;
}) {
  return (
    <>
      <div className="h-1.5 bg-gradient-to-r from-sky-400 via-blue-500 to-indigo-600" />
      <div className="space-y-3 p-4 sm:p-5">
        <h3 className="text-[11px] font-bold uppercase tracking-wider text-blue-600">Monthly Sulit</h3>
        <span className="inline-flex rounded-full bg-emerald-600 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">
          Most Popular
        </span>
        <CheckoutQuotedMonthlyBlock
          listMonthlyUsd={pr.price_pack_usd}
          billingCadence={billingCadence}
          currency={currency}
          formatMoney={formatMoney}
        />
        <p className="text-xs italic text-gray-500">included hours + add-ons</p>
        <div className="space-y-2 pt-1">
          <PlanMetricRow label="Included hours" suffix="hrs" middleClassName="text-blue-600 font-semibold tabular-nums">
            <span>{formatCount(pr.monthly_pack_hours)}</span>
          </PlanMetricRow>
          <PlanMetricRow
            label={`Add-on hrs (max ${formatCount(pr.max_monthly_extra_hours)})`}
            suffix="hrs"
            middleClassName="bg-white px-1 py-1 ring-blue-100/90"
          >
            <label htmlFor="checkout-monthly-extra" className="sr-only">
              Add-on hours beyond {formatCount(pr.monthly_pack_hours)}&nbsp;h pack, up to {formatCount(pr.max_monthly_extra_hours)}&nbsp;h
            </label>
            <input
              id="checkout-monthly-extra"
              type="number"
              min={0}
              max={pr.max_monthly_extra_hours}
              step={1}
              value={monthlyExtraHours}
              onChange={(e) => {
                const v = parseInt(e.target.value, 10);
                if (Number.isNaN(v)) onMonthlyExtraHoursChange(0);
                else onMonthlyExtraHoursChange(v);
              }}
              className="w-full max-w-[6.5rem] rounded-lg border border-blue-200/90 bg-white px-2 py-1.5 text-center text-sm font-semibold tabular-nums text-blue-700 shadow-sm focus:border-emerald-500/50 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
            />
          </PlanMetricRow>
          <PlanMetricRow
            label="Extra hour rate"
            suffix="/ hr"
            middleClassName="text-blue-600 font-semibold tabular-nums"
          >
            <span>{formatMoney(pr.price_extra_per_hour_usd, currency)}</span>
          </PlanMetricRow>
        </div>
        <div className="space-y-1.5 rounded-xl border border-blue-100/90 bg-blue-50/40 px-3 py-2.5 ring-1 ring-blue-100/70">
          <p className="text-[11px] text-gray-700">
            Total pool:{` `}
            {monthlyExtraHours > 0 ? (
              <>
                <span className="tabular-nums">
                  <span className="font-semibold text-gray-900">{formatCount(pr.monthly_pack_hours)}</span>
                  {` `}+{` `}
                  <span className="font-semibold text-gray-900">{monthlyExtraHours}</span>
                  {` `}={` `}
                  <span className="font-semibold tabular-nums text-emerald-800">{totalMonthlyHours}</span>
                </span>
              </>
            ) : (
              <>
                <span className="font-semibold tabular-nums text-emerald-800">{totalMonthlyHours}</span>
              </>
            )}
            {` `}
            h/month{' '}
            {monthlyExtraHours > 0 ? '(pack + add-on).' : '(pack).'}
          </p>
          {monthlyExtraHours > 0 ? (
            <p className="text-[11px] font-medium tabular-nums text-blue-900">
              Add-on ref.: {monthlyExtraHours} × {formatMoney(pr.price_extra_per_hour_usd, currency)}
              /h ≈ {formatMoney(extraHoursCostUsd, currency)}/mo
              {billingCadence === 'annual'
                ? ` + ≈ ${formatMoney(extraHoursCostUsd * 12, currency)}/yr`
                : ''}{' '}
              (excl. pack fee)
            </p>
          ) : null}
        </div>
      </div>
    </>
  );
}

function ChosenUnlimited({
  pr,
  currency,
  formatMoney,
  billingCadence,
}: {
  pr: RoleComputePresetDTO;
  currency: ProfileCurrency;
  formatMoney: (n: number, c: ProfileCurrency) => string;
  billingCadence: BillingCadenceDisplay;
}) {
  return (
    <>
      <div className="h-1.5 bg-gradient-to-r from-amber-400 via-orange-500 to-orange-600" />
      <div className="space-y-3 p-4 sm:p-5">
        <h3 className="text-[11px] font-bold uppercase tracking-wider text-orange-600">Unlimited Access</h3>
        <CheckoutQuotedMonthlyBlock
          listMonthlyUsd={pr.price_unlimited_usd}
          billingCadence={billingCadence}
          currency={currency}
          formatMoney={formatMoney}
        />
        <p className="text-xs italic text-gray-500">No cap. Max performance.</p>
        <PlanMetricRow label="Included hours" suffix="" middleClassName="text-orange-600 font-bold uppercase">
          <span className="text-[11px] font-bold uppercase tracking-tight sm:text-xs">Unlimited</span>
        </PlanMetricRow>
      </div>
    </>
  );
}
